import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaShieldAlt, FaCheckCircle, FaInfoCircle, FaArrowRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { goReserve } from '../../utils/navigation';
import NoticeContent from '../../components/NoticeContent';
import { useMedicinePrices, toSinglePriceItems } from '../../hooks/useMedicinePrices';

interface EdPrematurePageProps {
  ipAllowed: boolean;
}

/** 注意事項のバージョン（文面を更新したらここを更新。既存の同意は無効化され、再チェックが必要） */
const NOTICE_VERSION = 'v2025-10-01';

const EdPrematurePage: React.FC<EdPrematurePageProps> = ({ ipAllowed }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang: 'ja' | 'zh' = i18n.language.toLowerCase().startsWith('zh') ? 'zh' : 'ja';
  const navigate = useNavigate();

  // 薬品価格（DB優先、フォールバックは静的データ）
  const { medicines } = useMedicinePrices('ed_premature', lang);

  // ページ表示用の定型文（中日）
  const L = {
    ja: {
      helmetTitle: 'ED・男性アンチエイジング - Melodis Clinic',
      pageTitle: 'ED・男性アンチエイジング',
      description:
        'ED（勃起障害）は、性的な刺激があっても満足な勃起が得られない／維持できない状態です。心理的要因や血管・ホルモン・生活習慣などが関与します。休息やストレスコントロールで改善することもありますが、現代のライフスタイルでは難しい場合も多く、適切な薬物治療を併用してQOLの改善を目指します。',
      sectionTitle: 'ED・男性アンチエイジング',
      effectTitle: '効果と服用時の注意',
      effectBullets: [
        'ED薬はPDE5阻害により勃起を補助します。性的刺激・興奮がなければ効果は発現しません。',
        'できるだけ空腹時の内服が推奨です。アルコールは飲み過ぎに注意し、適量に。',
        'グレープフルーツなど一部柑橘類との同時摂取は避けてください。',
        'HIV治療薬・抗真菌薬・降圧薬など併用薬がある場合は必ず医師に相談してください。',
        '心疾患・脳梗塞の既往がある方は必ず医師に相談してください。（一部薬剤は年齢制限あり）',
      ],
      medsTitle: '主な治療薬と持続時間',
      // バルデナフィルを削除（提供外）
      medsBullets: [
        'シルデナフィル：服用後30分〜1時間で発現、持続約4〜5時間',
        'タダラフィル：30分〜1時間で発現、持続最大36時間',
      ],
      dailyTitle: '毎日少量タダラフィル',
      dailyText:
        'タダラフィル少量持続投与（2.5mg など）は、継続服用により血流を改善し、根治的な改善を目指す選択肢です。当日の即効性を求める場合は、単回投与の用量を検討します。',
      // ▼ 価格（あなた指定の最新値）
      priceTitle: '価格（診察料 0円）：',
      priceItems: [
        { group: 'continuous', name: 'タダラフィル (Tadalafil)', dosage: '2.5mg 30日分', price: 4200, taxPrice: 4620 },
        { group: 'continuous', name: 'タダラフィル (Tadalafil)', dosage: '5mg 30日分', price: 5200, taxPrice: 5720 },
        { group: 'single', name: 'シアリス (Cialis)', dosage: '10mg 1錠', price: 1980, taxPrice: 2178 },
        { group: 'single', name: 'シアリス (Cialis)', dosage: '20mg 1錠', price: 2080, taxPrice: 2288 },
        { group: 'single', name: 'シルデナフィル (Sildenafil)', dosage: '25mg 1錠', price: 420, taxPrice: 462 },
        { group: 'single', name: 'シルデナフィル (Sildenafil)', dosage: '50mg 1錠', price: 720, taxPrice: 792 },
        { group: 'single', name: 'バルデナフィル (Vardenafil)', dosage: '10mg 1錠', price: 1120, taxPrice: 1232 },
        { group: 'single', name: 'バルデナフィル (Vardenafil)', dosage: '20mg 1錠', price: 1220, taxPrice: 1342 },
      ],
      badges: ['診察料 0円', '24時間オンライン予約', 'スピード発送', '自由診療（保険適用外）'],
      faqBtn: 'FAQを見る',
      noticeBtn: '注意事項はこちらから',
      treatmentTitle: '治療の流れ・当院の特徴',
      treatments: [
        'オンライン診療で症状・既往歴・内服薬を確認',
        '医師が薬の種類・用量を提案',
        '最短次日から内服開始が可能',
        '副作用や併用禁忌を丁寧にご説明',
      ],
      recommendTitle: 'こんな方におすすめ',
      recommends: [
        '初めてED治療薬を試したい',
        '過去の薬で頭痛／動悸が強かった',
        '食事やお酒の影響を避けたい',
        '週末に合わせて柔軟に使いたい',
      ],
      cta: 'オンライン診療を予約する',
      modalTitle: 'ED・男性アンチエイジングの注意事項',
      modalAgree: '上記を読み、内容に同意します。',
      modalCta: '同意して予約に進む',
      modalClose: '閉じる',
    },
    zh: {
      helmetTitle: 'ED・男性抗衰老 - Melodis Clinic',
      pageTitle: 'ED・男性抗衰老',
      description:
        'ED 指在有性刺激的情况下仍难以获得或维持足够硬度的勃起。其成因可能涉及心理因素、血管/激素变化与生活方式等。休息与压力管理有助于改善，但在现代生活中往往不易做到，联合合适的药物治疗可提升生活质量（QOL）。',
      sectionTitle: 'ED・男性抗衰老',
      effectTitle: '疗效与服用注意',
      effectBullets: [
        'ED 药通过抑制 PDE5 辅助勃起；无性刺激或兴奋时不会起效。',
        '建议空腹时服用；饮酒请适量，避免过量。',
        '避免与葡萄柚等部分柑橘类食物同时摄入。',
        '如正在使用 HIV 治疗药、抗真菌药、降压药等，请务必先咨询医生。',
        '如既往有心脏病或脑卒中史，请务必事先咨询医生。（部分药物有限龄限制）',
      ],
      medsTitle: '主要药物与持续时间',
      // 去掉伐地那非（未提供）
      medsBullets: [
        '西地那非：服后约 30–60 分钟起效，维持约 4–5 小时',
        '他达拉非：服后约 30–60 分钟起效，最长可达 36 小时',
      ],
      dailyTitle: '他达拉非少量每日服用',
      dailyText:
        '他达拉非少量持续（如 2.5mg）可通过长期服用改善血流，作为更根本的改善方案；若当天需要更强即刻效果，可考虑单次用量。',
      // ▼ 价格（中文表述）
      priceTitle: '价格（诊察费 0 日元）：',
      priceItems: [
        { group: 'continuous', name: '他达拉非 (Tadalafil)', dosage: '2.5mg 30日份', price: 4200, taxPrice: 4620 },
        { group: 'continuous', name: '他达拉非 (Tadalafil)', dosage: '5mg 30日份', price: 5200, taxPrice: 5720 },
        { group: 'single', name: '希爱力 (Cialis)', dosage: '10mg 1片', price: 1980, taxPrice: 2178 },
        { group: 'single', name: '希爱力 (Cialis)', dosage: '20mg 1片', price: 2080, taxPrice: 2288 },
        { group: 'single', name: '西地那非 (Sildenafil)', dosage: '25mg 1片', price: 420, taxPrice: 462 },
        { group: 'single', name: '西地那非 (Sildenafil)', dosage: '50mg 1片', price: 720, taxPrice: 792 },
        { group: 'single', name: '伐地那非 (Vardenafil)', dosage: '10mg 1片', price: 1120, taxPrice: 1232 },
        { group: 'single', name: '伐地那非 (Vardenafil)', dosage: '20mg 1片', price: 1220, taxPrice: 1342 },
      ],
      badges: ['诊察费 0 日元', '24 小时在线预约', '急速发货', '自费诊疗（不适用医保）'],
      faqBtn: '查看 FAQ',
      noticeBtn: '查看注意事项',
      treatmentTitle: '就诊流程与本院特点',
      treatments: [
        '在线问诊确认症状/既往史/在用药',
        '医生推荐合适的药物与剂量',
        '最快次日即可开始服药',
        '详细说明不良反应与禁忌合用',
      ],
      recommendTitle: '适合人群',
      recommends: [
        '首次尝试 ED 治疗药',
        '曾因用药出现明显头痛/心悸',
        '希望尽量不受饮食/酒精影响',
        '想根据周末行程灵活安排',
      ],
      cta: '立即预约在线咨询',
      modalTitle: 'ED・男性抗衰老的注意事项',
      modalAgree: '我已阅读并同意上述内容。',
      modalCta: '同意并继续预约',
      modalClose: '关闭',
    },
  } as const;

  const treatments = L[lang].treatments;
  const recommends = L[lang].recommends;

  // 注意事項モーダル
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [agree, setAgree] = useState(false);

  // モーダル表示中は body スクロールロック + ESC で閉じる
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setNoticeOpen(false);
    if (noticeOpen) {
      window.addEventListener('keydown', onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }
  }, [noticeOpen]);

  const openNotice = () => {
    setAgree(false);
    setNoticeOpen(true);
  };

  const proceedReserve = () => {
    if (!agree) return;
    setNoticeOpen(false);
    // 予約ページへ：科目をプリセット＋注意事項同意済み＋紹介ページ由来
    goReserve(navigate, ipAllowed, {
      preselectService: 'ed_premature',
      preAgreed: true,
      state: { from: 'service' },
    });
  };

  // 価格表は DB(admin設定)優先。ED は「連続服用(毎日少量タダラフィル)」と「単回投与(頓服)」の2区分で表示するが、
  // DBの薬品モデルに区分フィールドが無いため、名称から推定して group を付与する
  // （タダラフィル系=continuous、それ以外=single。未分類は single に寄せて必ず表示し、非表示落ちを防ぐ）。
  // 空配列ならハードコード L[lang].priceItems にフォールバック。
  const dbItems = toSinglePriceItems(medicines, lang).map((it) => ({
    ...it,
    group: /タダラフィル|Tadalafil|他达拉非/i.test(it.name) ? 'continuous' : 'single',
  }));
  const hcItems: any[] = [...L[lang].priceItems];
  // 区分(連続/単回)単位でフォールバック: 該当区分にDB薬が有ればDB、無ければその区分のハードコード現行価を維持。
  const section = (pred: (x: any) => boolean): any[] => {
    const d = dbItems.filter(pred);
    return d.length > 0 ? d : hcItems.filter(pred);
  };

  return (
    <div className="pt-0 sm:pt-10 pb-10 sm:pb-20 bg-[#fff9f8] font-sans" translate="no">
      <Helmet>
        <title>{t('services.ed_premature.title')} - Melodis Clinic</title>
        <meta name="description" content={t('seo.ed_premature.description')} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: lang === 'ja' ? 'シルデナフィル・タダラフィル (ED治療薬)' : '西地那非・他达拉非 (ED 治疗药)',
            image: 'https://melodis-clinic.com/images/16.png',
            description: lang === 'ja'
              ? 'シルデナフィル（バイアグラジェネリック）やタダラフィル（シアリスジェネリック）などのED治療薬をオンライン診療で提供しています。'
              : '本院通过在线问诊提供西地那非（万艾可通用名）和他达拉非（希爱力通用名）等 ED 治疗药物。',
            brand: {
              '@type': 'Brand',
              name: 'Generic ED Medications'
            },
            offers: {
              '@type': 'Offer',
              url: window.location.href,
              priceCurrency: 'JPY',
              price: '3000', // Starting price
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'MedicalOrganization',
                name: 'Melodis Clinic',
                telephone: '+81-3-6908-6228',
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: '3-23-3 Higashi-Ikebukuro, 804',
                  addressLocality: 'Toshima-ku',
                  addressRegion: 'Tokyo',
                  postalCode: '170-0013',
                  addressCountry: 'JP'
                }
              }
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: lang === 'ja' ? 'バイアグラとシアリスの違いは何ですか？' : '万艾可（西地那非）和希爱力（他达拉非）有什么区别？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: lang === 'ja'
                    ? 'バイアグラ（シルデナフィル）は即効性に優れ、強い勃起を促します。シアリス（タダラフィル）は持続時間が最大36時間と長く、食事の影響を受けにくいのが特徴です。'
                    : '万艾可（西地那非）见效快，勃起力度强。希爱力（他达拉非）持续时间长达 36 小时，且基本不受饮食影响。'
                }
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'MedicalWebPage',
            potentialAction: {
              '@type': 'ReserveAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: 'https://melodis-clinic.com/reserve',
                inLanguage: ['ja', 'zh'],
                actionPlatform: [
                  'http://schema.org/DesktopWebPlatform',
                  'http://schema.org/MobileWebPlatform'
                ]
              },
              result: {
                '@type': 'MedicalReservation',
                name: 'ED Consultation'
              }
            }
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-5xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">
          {/* ヘッダー */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-sky-100 p-3">
              <FaShieldAlt className="text-sky-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              <span className="text-indigo-400 mr-2">│</span>{L[lang].pageTitle}
            </h1>
          </div>

          <div className="mb-8 flex items-center gap-3 text-left">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-1.5 text-xs md:text-sm text-slate-600 hover:bg-slate-100 transition"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              {t('seo.supervision_doctor')}
              <span className="text-[10px] text-slate-400 font-normal ml-1">{t('seo.supervision_title')}</span>
            </Link>
          </div>

          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8 bg-[#fff9f8]/50 border-l-4 border-sky-200 pl-4 py-2">
            {t('seo.ed_premature.summary')}
          </p>

          {/* 説明（短文） */}
          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8">
            {L[lang].description}
          </p>

          {/* 詳細ブロック */}
          <section className="rounded-2xl border border-sky-100 bg-[#fff9f8]/60 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FaInfoCircle className="text-sky-600 w-5 h-5" />
              <h2 className="text-2xl font-semibold text-sky-700">{L[lang].sectionTitle}</h2>
            </div>

            {/* badges */}
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm">
              {L[lang].badges.map((b, i) => (
                <li
                  key={i}
                  className="rounded-xl border border-sky-200 bg-white px-4 py-3 text-slate-800 text-center"
                >
                  {b}
                </li>
              ))}
            </ul>

            <div className="space-y-4 text-slate-800 leading-relaxed">
              {/* 効果・注意点 */}
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">{L[lang].effectTitle}</h3>
                <ul className="list-disc pl-5 space-y-2 text-slate-700">
                  {L[lang].effectBullets.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>

              {/* 薬の種類・発現時間（提供中の薬のみ） */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{L[lang].medsTitle}</h3>
                  <ul className="space-y-2 text-slate-700">
                    {L[lang].medsBullets.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">{L[lang].dailyTitle}</h3>
                  <p className="text-slate-700">{L[lang].dailyText}</p>
                </div>
              </div>

              {/* 価格 */}
              <div className="mt-2 rounded-xl border border-slate-200 bg-white p-5 md:p-8">
                <h3 className="text-xl font-semibold text-slate-800 mb-6">{L[lang].priceTitle}</h3>
                
                <div className="space-y-8">
                  <div>
                    <h4 className="font-bold text-sky-700 mb-3 border-b border-sky-100 pb-1">
                      {lang === 'ja' ? '【連続服用方案】' : '【连续服用方案】'}
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {section((item: any) => typeof item === 'object' && item.group === 'continuous').map((item: any, i: number) => (
                        <div key={i} className="rounded-xl bg-sky-50/30 p-4 border border-sky-100 flex flex-col hover:border-sky-200 transition">
                          <div className="flex justify-between items-start mb-2">
                            <span className="font-bold text-slate-800">{item.name}</span>
                          </div>
                          <div className="text-xs text-slate-500 mb-3">{item.dosage}</div>
                          <div className="mt-auto text-right">
                            <div className="flex items-baseline justify-end gap-1">
                              <span className="font-bold text-lg text-slate-900">{item.price.toLocaleString()}</span>
                              <span className="text-sm text-slate-600"> 日元</span>
                            </div>
                            <div className="text-[11px] text-slate-400 font-normal">
                              ({lang === 'ja' ? '税込' : '含税'} {item.taxPrice.toLocaleString()} 日元)
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-sky-700 mb-3 border-b border-sky-100 pb-1">
                      {lang === 'ja' ? '【単回投与（1錠〜）】' : '【单次服用（1片起）】'}
                    </h4>
                    <ul className="space-y-3 text-slate-700">
                      {section((item: any) => typeof item === 'object' && item.group === 'single').map((item: any, i: number) => (
                        <li key={i} className="flex justify-between items-start flex-wrap gap-2 py-1">
                          <span className="flex flex-col text-left">
                            <span className="font-bold text-slate-800">{item.name}</span>
                            <span className="text-[11px] text-slate-500">{item.dosage}</span>
                          </span>
                          <div className="text-right">
                            <div className="flex items-baseline gap-1 justify-end">
                              <span className="font-bold text-slate-900">{item.price.toLocaleString()}</span>
                              <span className="text-sm text-slate-600"> 日元</span>
                            </div>
                            <div className="text-[10px] text-slate-400 font-normal">
                              ({lang === 'ja' ? '税込' : '含税'} {item.taxPrice.toLocaleString()} 日元)
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* links: FAQ / 注意事項 */}
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate('/faq/ed')}
                    className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-[#fff9f8] px-4 py-2 text-sm font-medium text-sky-700 hover:bg-sky-100"
                  >
                    {L[lang].faqBtn} <FaArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={openNotice}
                    className="inline-flex items-center gap-2 rounded-full border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100"
                  >
                    {L[lang].noticeBtn} <FaArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* 治療の流れ・特徴 */}
          <div className="rounded-2xl p-6 md:p-8 mb-8 border border-sky-100 bg-[#fff9f8]/60">
            <h2 className="text-2xl font-semibold text-sky-700 mb-4">
              {L[lang].treatmentTitle}
            </h2>
            <div className="flex flex-wrap gap-3">
              {treatments.map((item, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-4 py-2 text-sm text-slate-800"
                >
                  <FaCheckCircle className="text-sky-500 w-4 h-4" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* おすすめ */}
          <div className="rounded-2xl p-6 md:p-8 bg-slate-50 border border-slate-100">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">
              {L[lang].recommendTitle}
            </h3>
            <ul className="grid md:grid-cols-2 gap-3">
              {recommends.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-700"
                >
                  <FaCheckCircle className="text-sky-500 w-5 h-5 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA（→ モーダル経由） */}
          {isAuthenticated && (
            <div className="mt-10 text-center">
              <button
                onClick={openNotice}
                className="inline-block bg-sky-600 hover:bg-sky-600 active:bg-sky-700 text-white px-10 py-3.5 text-base md:text-lg rounded-full font-semibold shadow-sm transition"
              >
                {L[lang].cta}
              </button>
              <div className="mx-auto mt-3 h-1 w-20 rounded-full bg-sky-300/60" />
            </div>
          )}
        </div>
      </div>

      {/* 注意事項モーダル（public/data/notices から読込） */}
      {noticeOpen && (
        <div
          className="fixed inset-0 z-[1000] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-title"
          onClick={() => setNoticeOpen(false)}
        >
          {/* overlay */}
          <div className="fixed inset-0 bg-black/40" />

          {/* content wrapper */}
          <div
            className="min-h-full flex items-start md:items-center justify-center p-4 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-[1001] w-full max-w-2xl bg-white rounded-2xl shadow-xl p-6 md:p-7 border border-slate-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 id="notice-title" className="text-xl md:text-2xl font-semibold text-slate-900">
                  {L[lang].modalTitle}
                </h3>
                <button
                  onClick={() => setNoticeOpen(false)}
                  aria-label="Close"
                  className="rounded-full px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  ×
                </button>
              </div>

              {/* JSON → NoticeContent 経由で描画（言語自動フォールバック＋キャッシュバスター） */}
              <NoticeContent service="ed_premature" lang={i18n.language} version={NOTICE_VERSION} />

              <label className="mt-4 flex items-start gap-2 text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>{L[lang].modalAgree}</span>
              </label>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  disabled={!agree}
                  onClick={proceedReserve}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-2.5 font-semibold text-white transition ${agree ? 'bg-sky-600 hover:bg-sky-600 active:bg-sky-700' : 'bg-sky-300 cursor-not-allowed'
                    }`}
                >
                  {L[lang].modalCta}
                </button>
                <button
                  onClick={() => setNoticeOpen(false)}
                  className="rounded-full border border-slate-300 px-6 py-2.5 text-slate-700 hover:bg-slate-50"
                >
                  {L[lang].modalClose}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EdPrematurePage;
